import { get } from 'lodash';
import { all, ascend, concat, equals, filter, map, omit, path, prop, reduce, sortWith, transpose, uniq } from 'ramda';
import * as constantTypes from '../constants/ConstantTypes';

export function getAudioNameFromPath(audioFilePath) {
    if (audioFilePath) {
        let match = audioFilePath.match(/[0-9]{17}_[\d]+_.*_.*.mp3/g);
        if (match) {
            let audioFilePathArray = audioFilePath.split('_');
            let result = `${audioFilePathArray[audioFilePathArray.length - 2]}_${audioFilePathArray[audioFilePathArray.length - 1]}`;
            return result;
        }
        let audioFilePathArray = audioFilePath.split('/');
        return audioFilePathArray[audioFilePathArray.length - 1];
    }
    return null;
}

export function getAudioFileFromXML(xml) {
    if (xml) {
        if (typeof xml === 'string') {
            const domParser = new DOMParser();
            xml = domParser.parseFromString(xml, 'text/xml');
        }
        let audio = xml.querySelector('attachment[type="audio"] src');
        if (audio) {
            audio = new XMLSerializer().serializeToString(audio);
            audio = audio.replace(/<src.*?>/g, '');
            audio = audio.replace('</src>', '');
            return audio;
        }
    }
    return null;
}

export function translateItemTypesName(itemTypesName) {
    let ret = '';
    switch (itemTypesName) {
        case 'exercise.multiplechoice':
            ret = constantTypes.QUESTION_MULTIPLE_CHOICE;
            break;
        case 'exercise.opengap':
            ret = constantTypes.QUESTION_OPEN_FILL;
            break;
        case 'exercise.closedgap':
            ret = constantTypes.QUESTION_CLOSE_FILL;
            break;
        case 'exercise.dragndrop':
            ret = constantTypes.QUESTION_POSITIONING;
            break;
        case 'exercise.finderror':
            ret = constantTypes.QUESTION_FIND_ERROR;
            break;
        case 'exercise.truefalse':
            ret = constantTypes.QUESTION_TRUE_FALSE;
            break;
        case 'exercise.barrage':
            ret = constantTypes.QUESTION_SEQUENCE;
            break;
        case 'exercise.checker':
            ret = constantTypes.QUESTION_CHECKER;
            break;
        case 'exercise.openanswer':
            ret = constantTypes.QUESTION_OPEN_QUESTION;
            break;
        case 'exercise.sharedtext':
            ret = constantTypes.SHAREDTEXT;
            break;
        default:
            ret = '';
            break;
    }
    return ret;
}

/**
 input: an array of objects:
 [
    {
        id: 1,
        name: 'Matematica',
        schoolLevel: {
            id: 1,
            name: 'Scuola secondaria di primo grado'
        }
    },{
        id: 2,
        name: 'Matematica',
        schoolLevel: {
            id: 2,
            name: 'Scuola secondaria di secondo grado'
        }
    }, {
        id: 3,
        name: 'Matematica',
        schoolLevel: {
            id: 2,
            name: 'Scuola secondaria di secondo grado'
        }
    }, {
        id: 4,
        name: 'Fisica',
        schoolLevel: {
            id: 2,
            name: 'Scuola secondaria di secondo grado'
        }
    }
 ]
 output: an array grouped per name:
 [
    {
        ids: [1],
        name: 'Matematica',
        schoolLevel: {
            id: 1,
            name: 'Scuola secondaria di primo grado'
        }
    }, {
        ids: [4],
        name: 'Fisica',
        schoolLevel: {
            id: 2,
            name: 'Scuola secondaria di secondo grado'
        }
    }, {
        ids: [2, 3],
        name: 'Matematica',
        schoolLevel: {
            id: 2,
            name: 'Scuola secondaria di secondo grado'
            }
    }
 ]
 */
export function groupByName(input) {
    const groupedSubjects = input.reduce((acc, obj) => {
        const subjectObjIndex = acc.findIndex(({ name, schoolLevel }) =>
            obj.schoolLevel ? name === obj.name && schoolLevel.name === obj.schoolLevel.name : name === obj.name
        );
        if (subjectObjIndex > -1) {
            acc[subjectObjIndex] = {
                ...acc[subjectObjIndex],
                ids: acc[subjectObjIndex].ids.concat(obj.id)
            };
            return acc;
        }
        return acc.concat({
            ...omit(['id'], obj),
            ids: [obj.id]
        });
    }, []);
    return sortWith([ascend(path(['schoolLevel', 'name'])), ascend(prop('name'))], groupedSubjects);
}

/**
 * input: an array of objects: [{id: 1, name: "Massimo Bergamini"}, {id: 2, name: "Anna Trifone"}, {id: 3, name: "Gabriella Barozzi"}]
 * output: the array sorted: [{id: 3, name: "Gabriella Barozzi"}, {id: 1, name: "Massimo Bergamini"}, {id: 2, name: "Anna Trifone"}]
 */
export function sort(input) {
    input.sort((a, b) => {
        const nameA = a.name.toUpperCase().split(' ')[0];
        const nameB = b.name.toUpperCase().split(' ')[0];
        const surnameA = a.name.toUpperCase().split(' ')[1];
        const surnameB = b.name.toUpperCase().split(' ')[1];
        return surnameA < surnameB ? -1 : surnameA > surnameB ? 1 : nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });
    return input;
}

export function validateExerciseContent(jsonContent, exerciseType, validations) {
    const validator = validations.find(x => x.exerciseTypeId === exerciseType);
    const [globalError] = validator.globalValidation
        .map(validator => {
            return validator(jsonContent);
        })
        .filter(error => {
            return error !== undefined;
        });

    const questionError = validator.questionValidation && validator.questionValidation(jsonContent);
    const fieldsErrors = validator.fieldValidations.map(validator => {
        return validator(jsonContent);
    });

    const fieldsErrorsObject = {
        errorMessagesList: filter(x => x !== undefined, uniq(reduce(concat, [], fieldsErrors))),
        correctAnswersList: map(all(equals(undefined)), transpose(fieldsErrors))
    };

    return {
        questionError,
        globalError,
        fieldsErrorsObject
    };
}

export function getSubjectNameFromExercises(exercisesArray) {
    if (Array.isArray(exercisesArray) && exercisesArray.length !== 0) {
        const subjectsNameArray = exercisesArray.map(exercise => get(exercise, 'subject.name'));
        const first = subjectsNameArray[0];

        let firstOccurenceSubjectsNameArray = [first];

        for (let i = 1; i < subjectsNameArray.length; i++) {
            if (first !== subjectsNameArray[i]) {
                if (!firstOccurenceSubjectsNameArray.includes(subjectsNameArray[i])) {
                    firstOccurenceSubjectsNameArray.push(subjectsNameArray[i]);
                }
            }
        }

        return firstOccurenceSubjectsNameArray;
    }

    return [];
}
